import { BettingApi, cancelRequest } from 'api';
import axios from 'axios';
import { getBetTypeForSelectedBets, getOutcomesAndBlocksAfterChange, getUserOddsChangeRules } from 'features/coupon/utility';
import i18next from 'i18next';
import { ThunkDispatch } from 'redux-thunk';
import { addNotification } from 'shared/components/toast';
import { AKO, MAX_WINNING, SYSTEM } from 'shared/constants/common';
import { BetslipEvents, setDataLayer } from 'shared/constants/datalayer';
import { BETSType, PlaceBet, State } from 'shared/interface';
import * as actionTypes from 'shared/store/action-types';
import { createAction, getRegisterMethod, getUserDetails, isLoggedin, setBetsDetail } from 'shared/util/utility';
import { OutcomeOddsUpdateState } from '../../features/coupon/interface';
import { getAnalyticalData } from '../util/analytical';

const addBet = (bets: BETSType) => {
	return async (dispatch: ThunkDispatch<{}, {}, any>, getState: () => State | {}) => {
		try {
			const numOfSlipsInBetslip = bets.blocks.flat().length + bets.outcomes.length;
			const removedBets = (getState() as State).coupon.analytics.removedBets;

			if (numOfSlipsInBetslip > 30) {
				addNotification('warning', i18next.t('maxBetWarning'));
				return;
			}

			const betsFromState = (getState() as State).coupon.bets;
			const coupon = (getState() as State).coupon;

			const currentMode = betsFromState.betMode;
			const nextMode = bets.betMode;

			const betType = bets.betMode === AKO ? getBetTypeForSelectedBets(bets.outcomes) : bets.betType;

			cancelRequest(`/betting/calculate-tax-winning/${getRegisterMethod()}`, 'Not Needed');

			const filteredBet = { ...bets, outcomes: bets.outcomes.filter((outcome) => !!outcome.eventName), betType };

			dispatch(createAction(actionTypes.ADD_BET, { bets: filteredBet }));
			const betStatics: any = (getState() as State).coupon.betStatics;

			const possibleWinning: any = (getState() as State).coupon.betStatics.possibleWinning;
			const totalOdds = (filteredBet.outcomes.reduce((acc, item) => (item.selectedBet ? acc * item.outcomeOdds : acc), 1) * 0.88).toFixed(2);
			dispatch(createAction(actionTypes.CALCULATE_TAX_INIT, { awaitingWinningResp: true }));

			if (currentMode === AKO && nextMode === SYSTEM) {
				dispatch(createAction(actionTypes.CALCULATE_TAX_INIT, { awaitingWinningResp: true }));
			} else {
				const win = (filteredBet.freeBetStack || filteredBet.stake) * Number(totalOdds);
				dispatch(
					createAction(actionTypes.CALCULATE_TAX_SUCCESS, {
						bets: filteredBet,
						betStatics: {
							...betStatics,
							possibleWinning: {
								...possibleWinning,
								totalOdds,
								winning: win < MAX_WINNING ? win : MAX_WINNING,
							},
						},
						skipCancelLoading: true,
					}),
				);
			}

			const response = await BettingApi.taxCalculation({
				...filteredBet,
				stake: filteredBet.freeBetStack || filteredBet.stake,
				blocks: filteredBet.blocks || [],
				...getUserOddsChangeRules(),
			});
			setBetsDetail(filteredBet);

			const refreshedOdds = response.data.data.refreshedOdds;

			const refreshedOutcomes = bets.outcomes.map(
				(outcome) => refreshedOdds.find((odds: PlaceBet) => odds.outcomeId === outcome.outcomeId) || outcome,
			);

			const refreshedBlocks = bets.blocks.map((block) =>
				block.map((bet) => refreshedOdds.find((odds: PlaceBet) => odds.outcomeId === bet.outcomeId) || bet),
			);

			dispatch(
				createAction(actionTypes.CALCULATE_TAX_SUCCESS, {
					betStatics: {
						taxFactor: response.data.data.taxFactor,
						possibleWinning: response.data.data.possibleWinning,
						newPossibleWinning: response.data.data.newPossibleWinning,
					},
					bets: { ...filteredBet, blocks: refreshedBlocks, outcomes: refreshedOutcomes },
				}),
			);

			if (isLoggedin()) {
				localStorage.setItem('action', 'true');
			}

			const betAnalytics = {
				analytics: coupon.analytics,
				betStatics: response.data.data,
				awaitingWinningResp: coupon.awaitingWinningResp,
				bets,
			};

			// betslip events
			const numOfBetsFromState = betsFromState.outcomes.length + betsFromState.blocks.flat().length;
			if (numOfBetsFromState === 0 && bets.outcomes.length > 0) {
				setDataLayer(BetslipEvents.BET_START(getAnalyticalData(betAnalytics)));
			}
			if (numOfBetsFromState > 0 && numOfSlipsInBetslip > numOfBetsFromState) {
				setDataLayer(BetslipEvents.BET_ADD_NEXT_EVENT(getAnalyticalData(betAnalytics, true), numOfSlipsInBetslip));
			}
			if (numOfBetsFromState > 0 && numOfSlipsInBetslip < numOfBetsFromState) {
				dispatch(createAction(actionTypes.ANALYTICS_REMOVE_BET));
				setDataLayer(BetslipEvents.BET_REMOVE(getAnalyticalData(coupon, true), removedBets));
			}
		} catch (err) {
			if (axios.isCancel(err)) {
				return;
			}
			console.error(err);
		}
	};
};

const createLoader = () => {
	return async (dispatch: ThunkDispatch<any, any, any>, getState: () => State | {}) => {
		dispatch(createAction(actionTypes.CALCULATE_TAX_INIT, { awaitingWinningResp: true }));
	};
};

const calculateTax = () => {
	return async (dispatch: ThunkDispatch<{}, {}, any>, getState: () => State | {}) => {
		try {
			dispatch(createAction(actionTypes.CALCULATE_TAX_INIT, { awaitingWinningResp: true }));
			const bets: BETSType = (getState() as State).coupon.bets;

			const response = await BettingApi.taxCalculation({
				...bets,
				stake: !!bets.freeBetStack ? bets.freeBetStack : bets.stake,
				blocks: bets.blocks || [],
				...getUserOddsChangeRules(),
			});

			const refreshedOdds = response.data.data.refreshedOdds;

			let totalOdds = 1;
			refreshedOdds.map((item: any) => {
				return (totalOdds *= item.selectedBet ? item.outcomeOdds : 1);
			});
			// const winning = (!!bets.freeBetStack ? bets.freeBetStack : bets.stake) * totalOdds * 0.88

			const refreshedOutcomes = bets.outcomes.map(
				(outcome) => refreshedOdds.find((odds: PlaceBet) => odds.outcomeId === outcome.outcomeId) || outcome,
			);

			const refreshedBlocks = bets.blocks.map((block) =>
				block.map((bet) => refreshedOdds.find((odds: PlaceBet) => odds.outcomeId === bet.outcomeId) || bet),
			);

			const activeSlipsInBetslip = getActiveSlipsAfterRefreshedOdds(refreshedBlocks, refreshedOutcomes);
			const updatedBets = { ...bets, ...activeSlipsInBetslip };

			setBetsDetail(updatedBets);

			dispatch(
				createAction(actionTypes.CHANGE_BET_OUTCOMES_SUCCESS, {
					bets: updatedBets,
				}),
			);
			dispatch(
				createAction(actionTypes.CALCULATE_TAX_SUCCESS, {
					betStatics: {
						taxFactor: response.data.data.taxFactor,
						possibleWinning: response.data.data.newPossibleWinning,
						newPossibleWinning: response.data.data.newPossibleWinning,
					},
					bets: updatedBets,
				}),
			);
		} catch (err) {
			console.error(err);
			dispatch(createAction(actionTypes.CALCULATE_TAX_FAIL));
		}
	};
};

const getActiveSlipsAfterRefreshedOdds = (blocks: PlaceBet[][], outcomes: PlaceBet[]) => {
	const activeEventsInBlocks = blocks.map((block) => block.filter((item) => !item.isFinished));
	const activeEventsInOutcomes = outcomes.filter((item: PlaceBet) => !item.isFinished);

	const updatedBlocksAndOutcomes = getOutcomesAndBlocksAfterChange(activeEventsInBlocks, activeEventsInOutcomes);

	return { blocks: updatedBlocksAndOutcomes.blocks, outcomes: updatedBlocksAndOutcomes.outcomes };
};

const changeInBetOutcomes = (data: any) => {
	return async (dispatch: ThunkDispatch<{}, {}, any>, getState: () => State | {}) => {
		dispatch(createAction(actionTypes.CHANGE_BET_OUTCOMES_INIT));
		const bets: BETSType = (getState() as State).coupon.bets;
		changeOutcomes(bets, data, dispatch);
	};
};

const changeOutcomes = (bets: BETSType, data: any, dispatch: ThunkDispatch<{}, {}, any>) => {
	bets.outcomes.forEach((outcomeData) => {
		const index = data.findIndex((item: PlaceBet) => Number(outcomeData.outcomeId) === Number(item.outcomeId));

		if (index !== -1) {
			outcomeData.outcomeUpdateState = OutcomeOddsUpdateState.ODDS_NOT_CHANGED; //for not showing up & down arrows when ODDS are change
			outcomeData.showWarning = true; // for showing warning odds are change
			outcomeData.outcomeOdds = Number(data[index].databaseOdds); // update odds
			// setTimeout(() => changeOutcomes(bets, [], dispatch), 4000) //for remove warning
		} else {
			outcomeData.showWarning = false;
		}
	});
	bets.blocks.map((block) =>
		block.forEach((bet: PlaceBet) => {
			const index = data.findIndex((slip: PlaceBet) => Number(bet.outcomeId) === Number(slip.outcomeId));

			if (index !== -1) {
				bet.outcomeUpdateState = OutcomeOddsUpdateState.ODDS_NOT_CHANGED;
				bet.showWarning = true;
				bet.outcomeOdds = Number(data[index].databaseOdds);
			} else {
				bet.showWarning = false;
			}
		}),
	);
	dispatch(
		createAction(actionTypes.CHANGE_BET_OUTCOMES_SUCCESS, {
			bets: { ...bets },
		}),
	);
};

const changeBetMode = (betMode: string = AKO) => {
	return async (dispatch: ThunkDispatch<{}, {}, any>) => {
		try {
			dispatch(createAction(actionTypes.CHANGE_BET_MODE, { betMode }));
		} catch (err) {
			console.error(err);
		}
	};
};

const removeFreeBetStack = () => {
	return async (dispatch: ThunkDispatch<{}, {}, any>) => {
		try {
			dispatch(createAction(actionTypes.REMOVE_FREE_BET_STACK));
		} catch (err) {
			console.error(err);
		}
	};
};

const updateUserDetails = () => {
	return async (dispatch: ThunkDispatch<{}, {}, any>) => {
		const userDetails = getUserDetails();
		dispatch({
			type: actionTypes.UPDATE_USER_DETAILS,
			payload: userDetails,
		});
	};
};

export { addBet, calculateTax, changeBetMode, changeInBetOutcomes, removeFreeBetStack, createLoader, updateUserDetails };
